import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Cloud, Search, RefreshCw, CheckCircle2, XCircle, FileText,
    Loader2, Package, ChevronRight, Clock, Ban, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { cloudOrdersAPI } from '../lib/api';

// Cloud Orders is a thin admin inbox — only three buckets.
// `pending_review` is the only state the admin can act on. Everything
// else lives off this page (approved → /drafts, rejected → archive).
const STATUS_META = {
    pending_review: { label: 'Pending',  color: 'bg-amber-50 text-amber-800 border-amber-200',         Icon: Clock },
    approved:       { label: 'Approved', color: 'bg-emerald-50 text-emerald-800 border-emerald-200',   Icon: CheckCircle2 },
    rejected:       { label: 'Rejected', color: 'bg-red-50 text-red-800 border-red-200',               Icon: Ban },
    converted:      { label: 'Converted',color: 'bg-emerald-50 text-emerald-800 border-emerald-200',   Icon: CheckCircle2 },  // legacy
};

const TABS = [
    { key: 'pending_review', label: 'Pending review', filter: { status: 'pending_review' } },
    { key: 'rejected',       label: 'Rejected',       filter: { status: 'rejected' } },
    { key: 'all',            label: 'All',            filter: {} },
];

const formatEur = (v) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

// ----- Status pill ------------------------------------------------------------
const StatusPill = ({ status }) => {
    const meta = STATUS_META[status] || { label: status, color: 'bg-gray-50 text-gray-800 border-gray-200', Icon: Clock };
    const { Icon } = meta;
    return (
        <Badge variant="outline" className={`${meta.color} font-medium`}>
            <Icon size={11} className="mr-1" /> {meta.label}
        </Badge>
    );
};

// ----- Detail modal -----------------------------------------------------------
const OrderDetail = ({ order, onClose, onApprove, onReject, busy, onJumpToDraft }) => {
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState('');

    useEffect(() => {
        setRejectMode(false);
        setReason('');
    }, [order?.id]);

    if (!order) return null;
    const snap = order.customer_snapshot || {};
    const addressParts = [
        snap.address_line1, snap.address_line2,
        [snap.postal_code, snap.city].filter(Boolean).join(' '),
        snap.country,
    ].filter(Boolean);

    const isPending = order.status === 'pending_review';
    const isApproved = order.status === 'approved';

    return (
        <Dialog open={!!order} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="cloud-order-detail">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <DialogTitle className="flex items-center gap-3">
                            <Cloud size={20} className="text-[#C08478]" />
                            <span className="font-mono">{order.order_number}</span>
                            <StatusPill status={order.status} />
                            {order.order_type === 'quotation' && (
                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] uppercase tracking-wider" data-testid="detail-order-type-quotation">
                                    <FileText size={10} className="mr-1" /> Quotation request
                                </Badge>
                            )}
                        </DialogTitle>
                        <span className="text-xs text-gray-500">{order.created_at?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                </DialogHeader>

                {/* Customer + delivery snapshot */}
                <div className="grid grid-cols-2 gap-4 text-sm" data-testid="detail-customer-block">
                    <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[2px] text-gray-500 font-semibold mb-1">Customer</p>
                        <p className="font-semibold text-[#1A1A1A]">{snap.company_name || '—'}</p>
                        <p className="text-gray-600">{snap.contact_name}</p>
                        <p className="text-gray-500 text-[12px]">{order.customer_email}</p>
                        {snap.phone && <p className="text-gray-500 text-[12px]">{snap.phone}</p>}
                        {snap.vat_number && <p className="text-gray-500 text-[12px]">VAT: {snap.vat_number}</p>}
                        {snap.siret_number && <p className="text-gray-500 text-[12px]">SIRET: {snap.siret_number}</p>}
                    </div>
                    <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[2px] text-gray-500 font-semibold mb-1">Ship to</p>
                        {addressParts.length > 0 ? addressParts.map((p, i) => (
                            <p key={i} className="text-gray-600">{p}</p>
                        )) : (
                            <p className="text-gray-400 italic">No delivery address provided</p>
                        )}
                    </div>
                </div>

                {/* Approved → link to the draft */}
                {isApproved && order.sale_id && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-center justify-between gap-3" data-testid="detail-approved-banner">
                        <div className="text-sm text-emerald-900">
                            <span className="font-semibold">{order.quotation_number}</span> created in Drafts & Quotations
                        </div>
                        <button
                            onClick={() => onJumpToDraft(order.sale_id)}
                            className="text-xs inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-medium"
                            data-testid="jump-to-draft-btn"
                        >
                            Open draft <ExternalLink size={12} />
                        </button>
                    </div>
                )}

                <div>
                    <p className="text-xs uppercase tracking-[2px] text-gray-500 font-semibold flex items-center gap-1.5 mb-2">
                        <Package size={12} /> Line items
                    </p>
                    <div className="border border-[#EFE5E1] rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[#FBF8F6] text-[11px] uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">SKU</th>
                                    <th className="px-3 py-2 text-left">Product</th>
                                    <th className="px-3 py-2 text-right">Qty</th>
                                    <th className="px-3 py-2 text-right">Unit</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody data-testid="detail-line-items">
                                {(order.items || []).map((li) => (
                                    <tr key={li.product_id} className="border-t border-[#F5EEEA]">
                                        <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{li.sku}</td>
                                        <td className="px-3 py-2">{li.name}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{li.qty}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatEur(li.unit_price)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEur(li.line_total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-[#EFE5E1] bg-[#FBF8F6]">
                                    <td colSpan={4} className="px-3 py-2.5 text-right text-sm font-semibold">Subtotal (HT)</td>
                                    <td className="px-3 py-2.5 text-right font-bold text-[#1A1A1A]" data-testid="detail-subtotal">{formatEur(order.subtotal)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {order.notes && (
                    <div className="text-sm text-gray-600 bg-[#FBF8F6] border border-[#EFE5E1] rounded-md p-3" data-testid="detail-notes">
                        <p className="text-xs uppercase tracking-[2px] text-gray-500 font-semibold mb-1">Customer notes</p>
                        {order.notes}
                    </div>
                )}

                {/* Actions — only on pending */}
                {isPending && (
                    <DialogFooter>
                        {rejectMode ? (
                            <div className="space-y-2 w-full">
                                <Textarea
                                    placeholder="Reason for rejection (visible internally only)"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="text-sm bg-[#FBF8F6] border-[#EFE5E1]"
                                    data-testid="reject-reason-input"
                                />
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setRejectMode(false)} disabled={busy} data-testid="reject-cancel-btn">Cancel</Button>
                                    <Button
                                        onClick={() => onReject(reason)}
                                        disabled={busy}
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                        data-testid="reject-confirm-btn"
                                    >
                                        {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Ban size={14} className="mr-1.5" />}
                                        Confirm rejection
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-end gap-2 w-full">
                                <Button
                                    variant="outline"
                                    onClick={() => setRejectMode(true)}
                                    disabled={busy}
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    data-testid="reject-btn"
                                >
                                    <XCircle size={14} className="mr-1.5" /> Reject
                                </Button>
                                <Button
                                    onClick={onApprove}
                                    disabled={busy}
                                    className="bg-[#1A1A1A] hover:bg-[#333] text-white"
                                    data-testid="approve-btn"
                                >
                                    {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                    Approve → Quotation
                                </Button>
                            </div>
                        )}
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ----- Page ------------------------------------------------------------------
const CloudOrders = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Honour a `?q=<order_number>` deep-link (sent by the B2B source chip
    // on the Drafts page). Pre-populates the search box AND defaults the
    // active tab to "all" so approved rows surface immediately.
    const initialSearch = searchParams.get('q') || '';
    const [tab, setTab] = useState(initialSearch ? 'all' : 'pending_review');
    const [search, setSearch] = useState(initialSearch);
    const [debounced, setDebounced] = useState(initialSearch);
    const [data, setData] = useState({ items: [], total: 0, status_counts: {} });
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Strip the `?q=` from the URL after we've consumed it so a manual
    // tab change later doesn't bounce the user back to "all".
    useEffect(() => {
        if (searchParams.get('q')) {
            const sp = new URLSearchParams(searchParams);
            sp.delete('q');
            setSearchParams(sp, { replace: true });
        }
        // Intentionally only run once on mount.
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 100 };
            const tabDef = TABS.find((t) => t.key === tab) || TABS[0];
            Object.assign(params, tabDef.filter || {});
            if (debounced) params.q = debounced;
            const { data: d } = await cloudOrdersAPI.list(params);
            setData(d);
        } catch {
            toast.error('Could not load cloud orders');
        } finally {
            setLoading(false);
        }
    }, [tab, debounced]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const refreshActive = async () => {
        if (!active) return;
        const { data: fresh } = await cloudOrdersAPI.get(active.id);
        setActive(fresh);
    };

    const onApprove = async () => {
        if (!active) return;
        setActionBusy(true);
        try {
            const { data: result } = await cloudOrdersAPI.approve(active.id);
            toast.success(`Approved · ${result.quotation_number} created in Drafts & Quotations`);
            await refreshActive();
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Approve failed');
        } finally {
            setActionBusy(false);
        }
    };

    const onReject = async (reason) => {
        if (!active) return;
        setActionBusy(true);
        try {
            await cloudOrdersAPI.reject(active.id, { reason });
            toast.success('Order rejected');
            await refreshActive();
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reject failed');
        } finally {
            setActionBusy(false);
        }
    };

    const onJumpToDraft = (saleId) => {
        // Navigate the admin straight to the freshly-created draft on the
        // Drafts page. Same-tab — keeps the audit trail tight.
        setActive(null);
        navigate(`/drafts/${saleId}/edit`);
    };

    const counts = useMemo(() => data.status_counts || {}, [data]);
    const countFor = (key) => key === 'all' ? (counts.all ?? 0) : (counts[key] ?? 0);

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="cloud-orders-page">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-1">B2B portal</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] flex items-center gap-2">
                        <Cloud size={26} className="text-[#C08478]" /> Cloud Orders
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Review incoming wholesale orders. Approving promotes them to Drafts &amp; Quotations.</p>
                </div>
                <Button variant="outline" onClick={fetchList} disabled={loading} data-testid="refresh-btn">
                    {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
                    Refresh
                </Button>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex bg-white border border-[#EFE5E1] rounded-full p-1" data-testid="cloud-orders-tabs">
                    {TABS.map((tdef) => (
                        <button
                            key={tdef.key}
                            onClick={() => setTab(tdef.key)}
                            data-testid={`cloud-orders-tab-${tdef.key}`}
                            className={`px-4 py-1.5 text-sm rounded-full transition-all ${
                                tab === tdef.key ? 'bg-[#1A1A1A] text-white' : 'text-gray-600 hover:text-[#1A1A1A]'
                            }`}
                        >
                            {tdef.label}
                            <span className={`ml-2 text-[10px] ${tab === tdef.key ? 'opacity-80' : 'opacity-60'}`}>
                                {countFor(tdef.key)}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="relative w-72">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Order #, email, company…"
                        className="pl-9 bg-white border-[#EFE5E1]"
                        data-testid="cloud-orders-search"
                    />
                </div>
            </div>

            <div className="bg-white border border-[#EFE5E1] rounded-lg overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400">
                        <Loader2 size={20} className="animate-spin mr-2" /> Loading orders…
                    </div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-16" data-testid="cloud-orders-empty">
                        <Cloud size={32} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm font-medium text-[#1A1A1A]">No orders in this view</p>
                        <p className="text-xs text-gray-500 mt-1">Submitted carts from the B2B portal will appear here.</p>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[#FBF8F6] text-[11px] uppercase tracking-[2px] text-gray-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Order #</th>
                                <th className="px-4 py-3 text-left">Customer</th>
                                <th className="px-4 py-3 text-right">Items</th>
                                <th className="px-4 py-3 text-right">Subtotal</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Submitted</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((o) => (
                                <tr
                                    key={o.id}
                                    onClick={() => setActive(o)}
                                    className="border-t border-[#F5EEEA] hover:bg-[#FBF8F6] cursor-pointer transition-colors"
                                    data-testid={`cloud-order-row-${o.order_number}`}
                                >
                                    <td className="px-4 py-3 font-mono text-[12px] font-semibold">{o.order_number}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium text-[#1A1A1A]">{o.customer_snapshot?.company_name || '—'}</p>
                                            {o.order_type === 'quotation' && (
                                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] uppercase tracking-wider px-1.5 py-0" data-testid={`row-type-quotation-${o.order_number}`}>
                                                    QT
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-gray-500">{o.customer_email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums">{o.item_count}</td>
                                    <td className="px-4 py-3 text-right tabular-nums font-medium">{formatEur(o.subtotal)}</td>
                                    <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                                    <td className="px-4 py-3 text-xs text-gray-500">{o.created_at?.slice(0, 16).replace('T', ' ')}</td>
                                    <td className="px-4 py-3 text-right text-gray-400"><ChevronRight size={14} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <OrderDetail
                order={active}
                busy={actionBusy}
                onClose={() => setActive(null)}
                onApprove={onApprove}
                onReject={onReject}
                onJumpToDraft={onJumpToDraft}
            />
        </div>
    );
};

export default CloudOrders;
